import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Modal,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { Client } from '@microsoft/microsoft-graph-client';
import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs
} from 'firebase/firestore';
import 'isomorphic-fetch';

const steps = [
  { label: 'Select Mailbox', description: 'Pick the mailbox you want to monitor.' },
  { label: 'Pick Folder', description: 'Choose the folder inside that mailbox.' },
  { label: 'Define Rules', description: 'Create or edit your filtering rules.' }
];

const initialForm = {
  displayName: '',
  allMessages: false,
  senderContains: '',
  subjectContains: '',
  bodyContains: '',
  hasAttachments: false,
  moveToFolderId: '',
  category: '',
  flagMessage: false,
  replyEnabled: false,
  saveToSentItems: false,
  replyFrom: '',  
  replySubject: '',
  replyBody: ''
};

export default function MailboxRuleManager({ accessToken }) {
  const { officeUser } = useAuth();
  const [showWizard, setShowWizard] = useState(false); // View toggle
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: mailbox
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm]           = useState('');
  const [mailboxes, setMailboxes]             = useState([]);
  const [loadingMailboxes, setLoadingMailboxes] = useState(false);
  const [mailboxName, setMailboxName]         = useState('');

  // Step 2: folder
  const [folders, setFolders]           = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState('');

  // Step 3: rules
  const [folderPairs, setFolderPairs] = useState([]);
  const [rules, setRules]             = useState([]);
  const [editingIdx, setEditingIdx]   = useState(null);
  
  const [ruleForm, setRuleForm] = useState(initialForm);

  // Global panel
  const [allRules, setAllRules] = useState([]);

  // Alerts
  const [alertOpen, setAlertOpen]     = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Graph helper
  const getClient = useCallback(() => {
    return Client.init({
      authProvider: done => done(null, accessToken)
    });
  }, [accessToken]);

  // Fetch all rules for the “All Rules” panel
  const fetchAllRules = useCallback(async () => {
    const snap = await getDocs(collection(db, 'monitoredFolders'));
    const list = [];
    snap.forEach(d => {
      const mailbox = d.id;
      (d.data().folderPairs || []).forEach(fp => {
        const folderName = fp.sourceFolder.displayName;
        const folderId = fp.sourceFolder.id;
        (fp.rules || []).forEach((r, idx) => {
          list.push({ mailbox, folderName, folderId, rule: r, ruleIndex: idx });
        });
      });
    });
    setAllRules(list);
  }, []);

  useEffect(() => { fetchAllRules(); }, [fetchAllRules]);

  // Preload own mailbox
  useEffect(() => {
    if (!officeUser?.username) return;
    (async () => {
      setMailboxName(officeUser.username);
      setLoadingFolders(true);
      try {
        const client = getClient();
        const fr = await client
          .api(`/users/${officeUser.username}/mailFolders?$top=100`)
          .get();
        setFolders(fr.value || []);
        const snap = await getDoc(doc(db, 'monitoredFolders', officeUser.username));
        setFolderPairs(snap.exists() ? snap.data().folderPairs || [] : []);
      } catch (e) {
        console.error('Pre-load failed:', e);
      } finally {
        setLoadingFolders(false);
      }
    })();
  }, [officeUser, getClient]);

  const handleGlobalDelete = async (ruleItem) => {
    if (!window.confirm(`Delete the rule "${ruleItem.rule.displayName}" from ${ruleItem.mailbox} → ${ruleItem.folderName}?`)) return;
    try {
      // Load monitoredFolders doc for this mailbox
      const snap = await getDoc(doc(db, 'monitoredFolders', ruleItem.mailbox));
      if (!snap.exists()) return;
  
      const folderPairs = snap.data().folderPairs || [];
      // Find correct folderPair
      const pairIdx = folderPairs.findIndex(fp => fp.sourceFolder.id === ruleItem.folderId);
      if (pairIdx < 0) return;
  
      const rulesArr = folderPairs[pairIdx].rules || [];
      // Remove the rule by index
      rulesArr.splice(ruleItem.ruleIndex, 1);
      folderPairs[pairIdx].rules = rulesArr;
      // If rules now empty, optionally remove the whole folderPair
      // folderPairs = folderPairs.filter(fp => (fp.rules && fp.rules.length));
  
      await setDoc(doc(db, 'monitoredFolders', ruleItem.mailbox), { folderPairs });
      setAlertMessage('Rule deleted.');
      await fetchAllRules();
    } catch (e) {
      setAlertMessage('Error deleting rule: ' + e.message);
    } finally {
      setAlertOpen(true);
    }
  };

  // Mailbox search
  const handleMailboxSearch = async () => {
    setLoadingMailboxes(true);
    try {
      const client = getClient();
      const resp = await client
        .api(`/users?$filter=startswith(displayName,'${searchTerm}') or startswith(mail,'${searchTerm}')`)
        .get();
      setMailboxes(resp.value);
    } catch {
      setMailboxes([]);
    } finally {
      setLoadingMailboxes(false);
    }
  };

  // Select mailbox
  const handleSelectMailbox = async mbx => {
    setMailboxName(mbx.mail);
    setSearchDialogOpen(false);
    setMailboxes([]);
    setSelectedFolderId('');
    setRules([]);
    setFolderPairs([]);
    setLoadingFolders(true);
    try {
      const client = getClient();
      const fr = await client
        .api(`/users/${mbx.mail}/mailFolders?$top=100`)
        .get();
      setFolders(fr.value || []);
      const snap = await getDoc(doc(db, 'monitoredFolders', mbx.mail));
      setFolderPairs(snap.exists() ? snap.data().folderPairs || [] : []);
    } catch (e) {
      setAlertMessage('Failed to load mailbox.');
      setAlertOpen(true);
    } finally {
      setLoadingFolders(false);
    }
  };

  // When folder is picked → load rules + jump to step 3
  useEffect(() => {
    if (!selectedFolderId) return;
    const pair = folderPairs.find(fp => fp.sourceFolder.id === selectedFolderId);
    setRules(pair ? pair.rules || [] : []);
    setActiveStep(2);
    setEditingIdx(null);
    setRuleForm(initialForm);
  }, [selectedFolderId, folderPairs]);

  // Rule form handlers
  const handleRuleFormChange = (f, v) =>
    setRuleForm(p => ({
      ...p,
      [f]: v,
      ...(f === 'allMessages' && v
        ? { senderContains: '', subjectContains: '', bodyContains: '', hasAttachments: false }
        : {})
    }));

  const handleAddOrUpdateRule = () => {
    if (!ruleForm.displayName.trim()) {
      setAlertMessage('Rule must have a name.');
      return setAlertOpen(true);
    }
    if (ruleForm.replyEnabled && (!ruleForm.replySubject || !ruleForm.replyBody)) {
      setAlertMessage('Reply subject & body required.');
      return setAlertOpen(true);
    }
    const updated = [...rules];
    if (editingIdx !== null) updated[editingIdx] = ruleForm;
    else updated.push(ruleForm);
    setRules(updated);
    setEditingIdx(null);
    setRuleForm(initialForm);
  };

  // Jump in from Global edit
  const handleGlobalEdit = async ({ mailbox, folderId, ruleIndex }) => {
    setShowWizard(true);
    setActiveStep(0);
    await handleSelectMailbox({ mail: mailbox });
    setSelectedFolderId(folderId);
    setTimeout(() => {
      setEditingIdx(ruleIndex);
      const pair = folderPairs.find(fp => fp.sourceFolder.id === folderId);
      const toEdit = pair && pair.rules ? pair.rules[ruleIndex] : null;
      setRuleForm(toEdit || initialForm);
      setActiveStep(2);
    }, 200);
  };

  const handleSaveSettings = async () => {
    const src = folders.find(f => f.id === selectedFolderId);
    const updatedPairs = [...folderPairs];
    const idx = updatedPairs.findIndex(fp => fp.sourceFolder.id === selectedFolderId);
    const newPair = {
      sourceFolder: { id: src.id, displayName: src.displayName, mailboxName },
      rules
    };
    if (idx >= 0) updatedPairs[idx] = newPair;
    else updatedPairs.push(newPair);
    setFolderPairs(updatedPairs);

    try {
      await setDoc(doc(db, 'monitoredFolders', mailboxName), { folderPairs: updatedPairs });
      setAlertMessage('✔ Settings saved & subscribed!');
      setShowWizard(false);
      setActiveStep(0);
      await fetchAllRules();
    } catch (e) {
      setAlertMessage('Save error: ' + e.message);
    } finally {
      setAlertOpen(true);
    }
  };

  const nextStep = () => setActiveStep(s => Math.min(s + 1, steps.length - 1));
  const prevStep = () => setActiveStep(s => Math.max(s - 1, 0));
  const handleCancelWizard = () => {
    setShowWizard(false);
    setActiveStep(0);
    setRuleForm(initialForm);
    setEditingIdx(null);
  };

  // Helper to describe a rule
  const describeRule = r => {
    const filters = r.allMessages
      ? ['All messages']
      : [
          r.senderContains && `From contains "${r.senderContains}"`,
          r.subjectContains && `Subject contains "${r.subjectContains}"`,
          r.bodyContains && `Body contains "${r.bodyContains}"`,
          r.hasAttachments && 'Has attachments'
        ].filter(Boolean);

    const actions = [
      r.moveToFolderId && 'Moves to folder',
      r.category && `Tags "${r.category}"`,
      r.flagMessage && 'Flags message',
      r.replyEnabled && `Auto-replies (“${r.replySubject}”)`
    ].filter(Boolean);

    return [filters.join(' • '), actions.join(', ')].filter(Boolean).join(' | ');
  };

  // ---------- UI ----------

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 900, margin: '0 auto' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        📬 Mailbox Rules
      </Typography>
      <Typography variant="subtitle" sx={{ mb: 2, fontWeight: 700 }}>
        Create rules here to manage emails when they arrive in your mailbox.
      </Typography>

      {/* ---- All Rules Panel ---- */}
      {!showWizard && (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              🔍 Current Mailbox Rules
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setShowWizard(true);
                setActiveStep(0);
                setRuleForm(initialForm);
                setEditingIdx(null);
              }}
            >
              New Mailbox Rule
            </Button>
          </Box>
          {allRules.length === 0 ? (
            <Typography color="textSecondary">No rules configured yet.</Typography>
          ) : (
            <List>
                {allRules.map((it, i) => (
                  <ListItem
                    key={i}
                    secondaryAction={
                      <>
                        <IconButton
                          edge="end"
                          onClick={() => handleGlobalEdit({
                            mailbox: it.mailbox,
                            folderId: it.folderId,
                            ruleIndex: it.ruleIndex
                          })}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleGlobalDelete(it)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemText
                      primary={<strong>{it.rule.displayName}</strong>}
                      secondary={
                        <>
                          <span style={{ fontWeight: 500 }}>{it.mailbox}</span>
                          {' → '}
                          <span style={{ fontWeight: 500 }}>{it.folderName}</span>
                          <br />
                          <span style={{ color: '#444' }}>{describeRule(it.rule)}</span>
                        </>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </Paper>
      )}

      {/* ---- Wizard Stepper ---- */}
      {showWizard && (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((step, idx) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography fontWeight={activeStep === idx ? 700 : 500}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ---- Stepper content ---- */}
          {activeStep === 0 && (
            <Box>
              <Typography sx={{ mb: 2 }}>
                Select which mailbox you’d like to monitor.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setSearchDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                {mailboxName ? `Change (${mailboxName})` : 'Search Mailbox'}
              </Button>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancelWizard} sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button onClick={nextStep} disabled={!mailboxName} variant="contained">
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                Selected mailbox: <strong>{mailboxName}</strong>
              </Typography>
              {loadingFolders ? (
                <CircularProgress />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Folder to monitor</InputLabel>
                  <Select
                    value={selectedFolderId}
                    onChange={e => setSelectedFolderId(e.target.value)}
                  >
                    {folders.map(f => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Only messages in this folder will be processed.
                  </FormHelperText>
                </FormControl>
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={prevStep}>Back</Button>
                <Button
                  onClick={nextStep}
                  disabled={!selectedFolderId}
                  variant="contained"
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                Selected mailbox: <strong>{mailboxName}</strong>
              </Typography>
              {/* Rule list for this folder */}
              <Typography variant="h6" sx={{ mb: 2 }}>Existing Rules</Typography>
              {rules.length === 0 ? (
                <Typography color="textSecondary" sx={{ mb: 2 }}>No rules yet.</Typography>
              ) : (
                <List dense>
                  {rules.map((r, i) => (
                    <ListItem
                      key={i}
                      secondaryAction={
                        <>
                          <IconButton onClick={() => {
                            setEditingIdx(i);
                            setRuleForm(r);
                          }}>
                            <EditIcon />
                          </IconButton>
                        </>
                      }
                    >
                      <ListItemText
                        primary={r.displayName}
                        secondary={describeRule(r)}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Add/Edit Rule Form */}
              <Box sx={{ mt: 4, p: 2, border: '1px solid #ccc', borderRadius: 1, background: '#f9f9fa' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {editingIdx === null ? '➕ Add New Rule' : '✏️ Edit Rule'}
                </Typography>
                <TextField
                  label="Rule Name"
                  fullWidth
                  value={ruleForm.displayName}
                  onChange={e => handleRuleFormChange('displayName', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ruleForm.allMessages}
                      onChange={e => handleRuleFormChange('allMessages', e.target.checked)}
                    />
                  }
                  label="Apply to every message"
                />
                {!ruleForm.allMessages && (
                  <>
                    <TextField
                      label="From contains"
                      fullWidth
                      value={ruleForm.senderContains}
                      onChange={e => handleRuleFormChange('senderContains', e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Subject contains"
                      fullWidth
                      value={ruleForm.subjectContains}
                      onChange={e => handleRuleFormChange('subjectContains', e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Body contains"
                      fullWidth
                      value={ruleForm.bodyContains}
                      onChange={e => handleRuleFormChange('bodyContains', e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ruleForm.hasAttachments}
                          onChange={e => handleRuleFormChange('hasAttachments', e.target.checked)}
                        />
                      }
                      label="Only if has attachments"
                    />
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Move to folder</InputLabel>
                  <Select
                    value={ruleForm.moveToFolderId}
                    onChange={e => handleRuleFormChange('moveToFolderId', e.target.value)}
                  >
                    <MenuItem value=""><em>Don’t move</em></MenuItem>
                    {folders.map(f => (
                      <MenuItem key={f.id} value={f.id}>{f.displayName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Category (tags)"
                  fullWidth
                  value={ruleForm.category}
                  onChange={e => handleRuleFormChange('category', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ruleForm.flagMessage}
                      onChange={e => handleRuleFormChange('flagMessage', e.target.checked)}
                    />
                  }
                  label="Flag message"
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel shrink>Auto‑reply?</InputLabel>
                  <Select
                    value={ruleForm.replyEnabled ? 'yes' : ''}
                    onChange={e => handleRuleFormChange('replyEnabled', e.target.value === 'yes')}
                    displayEmpty
                  >
                    <MenuItem value="">No</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                  </Select>
                </FormControl>

                {ruleForm.replyEnabled && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Send reply from</InputLabel>
                      <Select
                        value={ruleForm.replyFrom}
                        onChange={e => handleRuleFormChange('replyFrom', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value={mailboxName}>{mailboxName}</MenuItem>
                        {mailboxes.map(mbx => (
                          <MenuItem key={mbx.mail} value={mbx.mail}>
                            {mbx.mail}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Which mailbox/account to send the auto‑reply from.
                      </FormHelperText>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ruleForm.saveToSentItems}
                          onChange={e =>
                            handleRuleFormChange('saveToSentItems', e.target.checked)
                          }
                        />
                      }
                      label="Save reply to Sent Items"
                    />
                    <TextField
                      label="Reply Subject"
                      fullWidth
                      value={ruleForm.replySubject}
                      onChange={e => handleRuleFormChange('replySubject', e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Reply Body"
                      fullWidth
                      multiline
                      rows={3}
                      value={ruleForm.replyBody}
                      onChange={e => handleRuleFormChange('replyBody', e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {editingIdx !== null && (
                    <Button onClick={() => {
                      setEditingIdx(null);
                      setRuleForm(initialForm);
                    }}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddOrUpdateRule}
                  >
                    {editingIdx === null ? 'Add Rule' : 'Update Rule'}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={prevStep}>Back</Button>
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={!rules.length}
                >
                  Finish & Save
                </Button>
                <Button onClick={handleCancelWizard}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* --- Mailbox Search Modal --- */}
      <Modal open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)}>
        <Paper sx={{ position: 'relative', p: 3, width: 400, m: 'auto', mt: 10 }}>
          <IconButton
            onClick={() => setSearchDialogOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">Search for a Mailbox</Typography>
          <TextField
            label="Name or email"
            fullWidth
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm('')}><ClearIcon /></IconButton>
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ textAlign: 'right', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleMailboxSearch}
              disabled={loadingMailboxes}
            >
              {loadingMailboxes ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <List sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
            {mailboxes.map(mbx => (
              <ListItem
                button
                key={mbx.mail}
                onClick={() => {
                  handleSelectMailbox(mbx);
                  nextStep(); // advance only here
                }}
              >
                <ListItemText
                  primary={`${mbx.displayName || mbx.mail}`}
                  secondary={mbx.mail}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Modal>

      {/* --- Alert --- */}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={() => setAlertOpen(false)}>
        <Alert severity="info" onClose={() => setAlertOpen(false)}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
